import { produce } from 'immer';
import compose from 'lodash/fp/compose';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { arrayOf, func, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { Button } from 'reactstrap';

import { AGREEMENT } from '../../attachments/constants';
import ResolvedAttachments from '../../attachments/ListAttachments';
import { withGuard } from '../../generic/change-guard';
import { withDocumentTitle } from '../../generic/DocumentTitle';
import Loading from '../../generic/Loading';
import Titled from '../../layout/Titled';
import {
  transformForPatch,
  transformForRendering,
} from '../../opportunities/transform-opportunity-helpers';
import { agreementShape, merchantShape } from '../../prop-types';
import api from '../../REST/api';
import AuthorizedLink from '../../REST/AuthorizedLink';
import withResolved from '../../REST/with-resolved';
import { tapFlashError, tapFlashSuccess } from '../../utils/flash';
import getEventTargetValue from '../../utils/get-event-target-value';
import AgreementBudgetProgress from '../AgreementBudgetProgress';
import AgreementForm from '../AgreementForm';
import AgreementRelatedOpportunities from '../AgreementRelatedOpportunities';
import CreateOpportunityLinkedToAgreement from '../CreateOpportunityLinkedToAgreement';
import { getTransformedAgreement } from '../transform-agreement-helpers';

export class ShowAgreementComponent extends Component {
  state = {
    opportunities: {},
    loadingOpportunities: true,
  };

  async componentDidMount() {
    await this.updateOpportunities();
  }

  async componentDidUpdate(prevProps) {
    if (this.props.model.id !== prevProps.model.id) {
      await this.updateOpportunities();
    }
  }

  handleOpportunityChange = e => {
    const { name } = e.target;
    const value = getEventTargetValue(e.target);

    this.setState(state =>
      produce(state, mutableState =>
        set(mutableState, `opportunities.${name}`, value),
      ),
    );
    this.props.guard.mark();
  };

  handleSaveOpportunities = async () => {
    try {
      await Promise.all(
        Object.values(this.state.opportunities).map(async opportunity => {
          const transformedOpportunity = await transformForPatch(opportunity);
          return api
            .patchOpportunity({
              id: opportunity.id,
              data: transformedOpportunity,
            })
            .catch(tapFlashError())
            .catch(() => {
              tapFlashError()(
                new Error(
                  `The above error occurred while saving ${opportunity.name}`,
                ),
              );
            });
        }),
      );

      tapFlashSuccess('All opportunities saved successfully')();
      this.props.guard.reset();
      // eslint-disable-next-line no-empty
    } catch (e) {}
  };

  async updateOpportunities() {
    this.setState({ loadingOpportunities: true });
    try {
      const opportunities = await Promise.all(
        this.props.model.agreementOpportunities.map(({ opportunity: id }) =>
          api.getOpportunity({ id }),
        ),
      );

      this.setState({
        opportunities: keyBy(
          opportunities.map(opp =>
            transformForRendering(opp, this.props.merchants),
          ),
          'id',
        ),
      });
    } catch (e) {
      tapFlashError()(e);
    } finally {
      this.setState({ loadingOpportunities: false });
    }
  }

  render() {
    const { model } = this.props;
    const { opportunities, loadingOpportunities } = this.state;
    const agreementId = model.id;
    const opportunitiesList = Object.values(opportunities);
    return (
      <>
        <div className="d-flex justify-content-between bg-white py-3 mb-1 mt-n3">
          <h3>
            {model.type} - {model.name}
          </h3>
          <AuthorizedLink
            to="edit"
            className="btn btn-secondary"
            query={api.patchAgreement}
          >
            Edit
          </AuthorizedLink>
        </div>
        <div className="sticky-top c-secondary-sticky-navbar bg-white py-3 mb-1 mt-n3">
          <AgreementBudgetProgress
            opportunities={opportunitiesList}
            agreement={model}
          />
        </div>
        <div className="mb-5" />
        <AgreementForm model={model} />
        <div className="mb-5" />
        <ResolvedAttachments
          titleSize="sm"
          contentType={AGREEMENT}
          objectId={agreementId}
        />
        <div className="mb-5" />
        {loadingOpportunities ? (
          <Loading />
        ) : (
          <Titled
            title="Related Opportunities"
            sticky
            className="c-agreement__position-after-progress-bar"
            actions={[
              <CreateOpportunityLinkedToAgreement agreement={model} />,
              <Button className="ml-2" onClick={this.handleSaveOpportunities}>
                Save
              </Button>,
            ]}
          >
            <AgreementRelatedOpportunities
              opportunities={opportunitiesList}
              agreementId={agreementId}
              onChange={this.handleOpportunityChange}
            />
          </Titled>
        )}
      </>
    );
  }
}

ShowAgreementComponent.propTypes = {
  model: agreementShape.isRequired,
  guard: shape({
    mark: func.isRequired,
    reset: func.isRequired,
  }).isRequired,
  merchants: arrayOf(merchantShape).isRequired,
};

const ShowAgreement = compose(
  withResolved({
    query: getTransformedAgreement,
    deriveQueryArgs: props => ({
      id: Number.parseInt(props.match.params.id, 10),
    }),
    as: 'model',
  }),
  withGuard,
  withDocumentTitle(({ model }) => model.name),
)(ShowAgreementComponent);

ShowAgreement.propTypes = {
  match: shape({ params: shape({ id: string.isRequired }).isRequired })
    .isRequired,
};

export default ShowAgreement;
